import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Order, ReorderMode, orderService } from '../../services';

interface ReorderDialogProps {
  open: boolean;
  order: Order | null;
  /** Number of items currently in the user's cart. Drives whether we show
   *  the merge/replace radio group. */
  cartItemCount: number;
  onClose: () => void;
  /** Called once the reorder POST has succeeded — typically refreshes the
   *  cart and navigates to the cart page. The argument is the human-friendly
   *  status string the parent should surface in a snackbar. */
  onSuccess: (statusMessage: string) => void;
}

/**
 * Confirmation dialog for "Reorder" / "Pesan Ulang".
 *
 * When the cart is empty we just show a confirm button. When it's non-empty
 * we ask the customer whether to merge the past order into the current cart
 * or replace it outright.
 */
const ReorderDialog: React.FC<ReorderDialogProps> = ({
  open,
  order,
  cartItemCount,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ReorderMode>('merge');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset transient state every time the dialog opens for a fresh run.
  React.useEffect(() => {
    if (open) {
      setMode('merge');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const itemCount = order?.item_count ?? 0;
  const hasCart = cartItemCount > 0;

  const handleConfirm = async () => {
    if (!order) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await orderService.reorder(order.id, mode);

      // Build a friendly summary for the parent's snackbar.
      let summary: string;
      if (res.mode === 'replace') {
        summary = t('orders.reorderResultReplaced', {
          count: res.added_count,
        });
      } else if (res.merged_count > 0) {
        summary = t('orders.reorderResultMerged', {
          added: res.added_count,
          merged: res.merged_count,
        });
      } else {
        summary = t('orders.reorderResultAdded', { count: res.added_count });
      }

      if (res.skipped && res.skipped.length > 0) {
        summary +=
          ' · ' +
          t('orders.reorderResultSkipped', { count: res.skipped.length });
      }

      onSuccess(summary);
    } catch (e: any) {
      setError(e?.message || t('errors.reorderFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('orders.reorderTitle')}</DialogTitle>
      <DialogContent>
        {order && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              #{order.order_number}
            </Typography>
          </Box>
        )}

        <Typography variant="body2" sx={{ mb: 2 }}>
          {hasCart
            ? t('orders.reorderConfirmHasCart', {
                cartCount: cartItemCount,
                count: itemCount,
              })
            : t('orders.reorderConfirmEmpty', { count: itemCount })}
        </Typography>

        {hasCart && (
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <RadioGroup
              value={mode}
              onChange={(e) => setMode(e.target.value as ReorderMode)}
            >
              <FormControlLabel
                value="merge"
                control={<Radio />}
                disabled={submitting}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {t('orders.reorderModeMerge')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('orders.reorderModeMergeHelp')}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
              <FormControlLabel
                value="replace"
                control={<Radio />}
                disabled={submitting}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {t('orders.reorderModeReplace')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('orders.reorderModeReplaceHelp')}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </RadioGroup>
          </FormControl>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || !order}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          {submitting
            ? t('orders.reorderProcessing')
            : t('orders.reorderConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReorderDialog;
